import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { getProductosPublic } from "../../services/public/ProductosPublic";
import ProductGrid from "../../components/store/ProductGrid";
import { useConfiguracionTiendaPublic } from "./hooks/useConfiguracionTiendaPublic";
import "./Store.css";

const PAGE_SIZE = 20;

function StoreHome() {
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const { configuracionTienda } = useConfiguracionTiendaPublic();
  const offsetRef = useRef(0);
  const fetchRequestIdRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useOutletContext() || {};

  const search = String(ctx.search ?? "");
  const category = ctx.category ?? null;
  const brand = ctx.brand ?? null;
  const subcategory = ctx.subcategory ?? null;
  const onlyOffers = !!ctx.oferta;
  const onlyDestacados = !!ctx.destacados;

  const normalizeResponse = (data) => {
    if (data && Array.isArray(data.items)) {
      return {
        items: data.items,
        hasMore:
          typeof data.hasMore === "boolean"
            ? data.hasMore
            : data.items.length === PAGE_SIZE,
      };
    }

    if (data && Array.isArray(data.data)) {
      return {
        items: data.data,
        hasMore:
          typeof data.hasMore === "boolean"
            ? data.hasMore
            : data.data.length === PAGE_SIZE,
      };
    }

    if (Array.isArray(data)) {
      return {
        items: data,
        hasMore: data.length === PAGE_SIZE,
      };
    }

    return { items: [], hasMore: false };
  };

  const loadPage = async ({ reset = false } = {}) => {
    if (!reset && !hasMore) return;
    if (!reset && status === "loading") return;

    const requestId = ++fetchRequestIdRef.current;

    try {
      setStatus("loading");
      setError(null);

      const nextOffset = reset ? 0 : offsetRef.current;

      const data = await getProductosPublic({
        limit: PAGE_SIZE,
        offset: nextOffset,
        search,
        category,
        brand,
        subcategory,
        oferta: onlyOffers,
        destacados: onlyDestacados,
      });

      if (requestId !== fetchRequestIdRef.current) return;

      const { items, hasMore: more } = normalizeResponse(data);

      setProducts((prev) => (reset ? items : [...prev, ...items]));
      setHasMore(more);
      offsetRef.current = nextOffset + items.length;
      setStatus("ok");
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error("Error loading products:", err);
      setError(err);
      setStatus("error");
    }
  };

  useEffect(() => {
    setProducts([]);
    setHasMore(true);
    offsetRef.current = 0;
    loadPage({ reset: true });
    // loadPage se excluye a propósito: depende de hasMore/status y volver a
    // ejecutar al cambiar su identidad provocaría recargas en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, brand, subcategory, onlyOffers, onlyDestacados]);

  const hasFilters = Boolean(
    search.trim() || category || brand || subcategory || onlyOffers || onlyDestacados
  );

  return (
    <div className="store-home-scope">
      <div className="store-home">
        <header className="store-home__head">
          <h1 className="store-home__title">
            {onlyOffers
              ? "Ofertas"
              : onlyDestacados
              ? "Productos destacados"
              : "Productos"}
          </h1>
        </header>

        {status === "error" && (
          <div className="store-home__state store-home__state--error">
            <div className="store-home__state-title">
              No pudimos cargar los productos.
            </div>
            <div className="store-home__error-msg">
              {String(error?.response?.data?.message || error?.message || "")}
            </div>
          </div>
        )}

        {status !== "error" && (
          <div className="store-home__grid-wrap">
            <ProductGrid
              products={products}
              configuracionTienda={configuracionTienda}
              loading={status === "loading" && products.length === 0}
              skeletonCount={12}
              onSelect={(p) =>
                navigate(`/store/product/${p.producto_id}`, {
                  // Conserva los filtros activos (query string) para que el
                  // botón "Regresar" del detalle devuelva a la misma vista.
                  state: { fromSearch: location.search },
                })
              }
            />
          </div>
        )}

        {status === "ok" && products.length === 0 && (
          <div className="store-home__state">
            {hasFilters
              ? "No encontramos productos con esos filtros."
              : "Aún no hay productos disponibles."}
          </div>
        )}

        {products.length > 0 && hasMore && (
          <div className="store-home__load-more-wrap">
            <button
              type="button"
              className="store-home__load-more"
              onClick={() => loadPage()}
              disabled={status === "loading"}
            >
              {status === "loading" ? "Cargando..." : "Ver más"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreHome;